import React, {MouseEvent, useCallback, useEffect, useRef, useState} from 'react';
import {NavigationProps} from "../constants/Props";
import Screen from "../components/Screen";
import Title from "../components/Title";
import TextInput from "../components/TextInput";
import Box from "../components/Box";
import {IAuthStore} from "../stores/AuthStore";
import {formatI18nKeys, toI18N} from "../utils/I18n";
import Alert, {AlertRefProps} from "../components/Alert";
import {IDeeplinkStore} from "../stores/DeeplinkStore";
import {showErrorToast, showInfoToast, usePrevious} from "../utils/HooksHelper";
import * as Analytics from "../utils/Analytics";

interface Props extends NavigationProps {
    store: IAuthStore,
    deeplinkStore: IDeeplinkStore,
    openSelectGroup?: (event: MouseEvent) => void
    openCreateGroup?: (event: MouseEvent) => void
    openGetStartedScreen?: (event: MouseEvent) => void
}

function ConfirmationCodeScreen(props: Props) {

    // Props

    const {
        openSelectGroup,
        openCreateGroup,
        openGetStartedScreen,
        deeplinkStore: {
            isNewGroup
        },
        onBack,
        store: {
            fullPhoneNumber,
            returnToGetStartedScreen,
            setReturnToGetStartedScreen,
            verificationToken,
            verifyPhoneNumberAndVerificationCode
        },
        isActive,
    } = props;

    const prevIsActive = usePrevious(isActive);
    useEffect(() => {
        if (!prevIsActive && isActive) {
            Analytics.logEvent(Analytics.events.land_on_verify_phone_number_page);
        }
    });


    // State

    const [isLoading, setIsLoading] = useState(false);
    const [verificationCode, setVerificationCode] = useState(verificationToken);

    // Ref

    const alertRef = useRef<AlertRefProps>(null);

    // Callbacks

    const info = useCallback(
        (text) => {
            showInfoToast(alertRef, text);
        },
        [alertRef],
    );
    const error = useCallback(
        (text) => {
            showErrorToast(alertRef, text);
        },
        [alertRef],
    );

    const handleOnNext = useCallback(
        async (e) => {
            const input = document.getElementById('confirmation-code-input-confirmation-code-screen');
            try {
                setIsLoading(true);
                const accountExists = await verifyPhoneNumberAndVerificationCode(verificationCode);
                if (accountExists && returnToGetStartedScreen) {
                    setReturnToGetStartedScreen(false);
                    openGetStartedScreen && openGetStartedScreen(e);
                } else if (accountExists && isNewGroup) {
                    openCreateGroup && openCreateGroup(e);
                } else if (accountExists) {
                    openSelectGroup && openSelectGroup(e);
                } else {
                    setIsLoading(false);
                    setVerificationCode('');
                    input && input.blur();
                    info(toI18N("confirmation_code_account_is_not_found"));
                }
            } catch (e) {
                setIsLoading(false);
                if (e.timeout) {
                    error(toI18N("general_timeout_error"));
                    return
                }
                if (e.status === 400) {
                    input && input.blur();
                    error(toI18N("confirmation_code_invalid"));
                    return
                }
                console.error(e)
            }
        },
        [
            isNewGroup,
            info,
            error,
            returnToGetStartedScreen,
            setReturnToGetStartedScreen,
            openGetStartedScreen,
            openCreateGroup,
            openSelectGroup,
            verificationCode,
            verifyPhoneNumberAndVerificationCode
        ],
    );

    return (
        <Screen
            title={"confirmation_code_screen_header_title"}
            subtitle={"confirmation_code_screen_header_subtitle"}
            isLoading={isLoading}
            onBack={onBack}
            disabledNext={verificationCode.length !== 6} onNext={handleOnNext}>

            <Alert ref={alertRef}/>

            <Box center topMargin={116}>
                <Title shrink title={toI18N("confirmation_code_title")}
                       subtitle={formatI18nKeys("confirmation_code_subtitle", fullPhoneNumber)}/>
            </Box>

            <Box grow top sideMargin topMargin={54}>
                <TextInput
                    maxLength={6}
                    value={verificationCode}
                    onChange={setVerificationCode}
                    inputMode={"numeric"}
                    autoComplete={"off"}
                    placeholder={toI18N("confirmation_code_input_placeholder")}
                    id={"confirmation-code-input-confirmation-code-screen"}/>
            </Box>

        </Screen>
    );
}

export default ConfirmationCodeScreen;
